import { ComponentType, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, RouteProps } from "react-router-dom";
import { clearStorage } from "@utils/storage";
import { UserContext } from "@context/UserContext";
import { Loader } from "@common/Loader/Loader";
import { Header } from "@common/Header/Header";
import { keycloakUtils, useKeycloak } from "itsf-ui-common";
import { Error } from "@common/HandleErrorAndLoading/Error";
import { FeatureFlagProvider } from "@common/FeatureFlagProvider/FeatureFlagProvider";

// Exact same interface as RouteProps except that component is not optional
interface IParams extends Pick<RouteProps, Exclude<keyof RouteProps, "component">> {
    component: ComponentType;
}

interface ICustomRoute extends IParams {
    isKeycloakReady: boolean;
}

const CustomRoute = ({ component: Component, isKeycloakReady, ...props }: ICustomRoute) => (
    <Route
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
        render={() => (
            <Fragment>
                {!isKeycloakReady && (
                    <div className="keycloak-loader">
                        <Loader size={70} />
                    </div>
                )}
                {isKeycloakReady && (
                    <Fragment>
                        <Header logoutCallback={clearStorage} />
                        <Component />
                    </Fragment>
                )}
            </Fragment>
        )}
    />
);

const CustomRouteAuthContext = ({ component, ...props }: IParams) => {
    const { isKeycloakLoading, userProfile, hasKeycloakFailed } = useKeycloak("login-required");
    const isKeycloakReady = !isKeycloakLoading && !hasKeycloakFailed;
    const userInfo = userProfile ?? {
        username: undefined,
        email: undefined,
        firstName: undefined,
        lastName: undefined,
        keycloakUuid: "",
        keycloakRoles: {},
        keycloakChannels: [],
    };

    return (
        <UserContext.Provider value={{ ...userInfo }}>
            <FeatureFlagProvider isKeycloakReady={isKeycloakReady}>
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <CustomRoute {...props} component={component} isKeycloakReady={isKeycloakReady} />
            </FeatureFlagProvider>
        </UserContext.Provider>
    );
};

const CustomRouteNoAuthContext = ({ component, ...props }: IParams) => {
    const { isKeycloakLoading, hasKeycloakFailed } = useKeycloak("check-sso");
    const isKeycloakReady = !isKeycloakLoading && !hasKeycloakFailed;

    return (
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        <CustomRoute {...props} component={component} isKeycloakReady={isKeycloakReady} />
    );
};

// Create route reachable only when auth
export const AuthRoute = ({ component, ...props }: IParams) => {
    const { t } = useTranslation("common");

    return global.Keycloak ? (
        <CustomRouteAuthContext
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...props}
            component={component}
        />
    ) : (
        <Error error={{ errorMessage: t("genericError") }} />
    );
};

// Create route reachable only when non auth
export const NonAuthRoute = ({ component, ...props }: IParams) => {
    return global.Keycloak ? (
        keycloakUtils.isAuthenticated() ? (
            <Redirect to="/" />
        ) : (
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            <CustomRouteNoAuthContext {...props} component={component} />
        )
    ) : (
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        <CustomRoute {...props} component={component} isKeycloakReady />
    );
};
